import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { congressTradesService } from '../../services/congressTradesService';
import { SectionSkeleton, SectionError } from './CompanyProfile';
import type { CongressTrade } from '../../types/congressTrades';
import { fmtUsDate } from './format';

interface Props { symbol: string; }

const PER_PAGE = 10;

function getMemberName(t: CongressTrade): string {
  if (t.representative_name) return t.representative_name;
  return `${t.senator_first_name ?? ''} ${t.senator_last_name ?? ''}`.trim() || 'Unknown';
}

function txStyle(type: string): { bg: string; text: string } {
  const t = type?.toLowerCase() ?? '';
  if (t === 'purchase') return { bg: 'bg-bull-blue/15', text: 'text-bull-blue' };
  if (t === 'sale') return { bg: 'bg-bear-red/15', text: 'text-bear-red' };
  return { bg: 'bg-accent-yellow/15', text: 'text-accent-yellow' };
}

function Pagination({ page, totalPages, onPrev, onNext }: {
  page: number; totalPages: number; onPrev: () => void; onNext: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 mt-3 justify-end">
      <button
        disabled={page === 1}
        onClick={onPrev}
        className="px-2 py-1 text-xs rounded border border-dark-border text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        &larr; Prev
      </button>
      <span className="text-xs text-gray-400">{page} / {totalPages}</span>
      <button
        disabled={page >= totalPages}
        onClick={onNext}
        className="px-2 py-1 text-xs rounded border border-dark-border text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        Next &rarr;
      </button>
    </div>
  );
}

export function CongressTradesSection({ symbol }: Props): React.ReactElement {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerCongressInline', symbol, page],
    queryFn: () => congressTradesService.fetchTrades({ page, perPage: PER_PAGE, search: symbol }),
    staleTime: 5 * 60 * 1000,
    enabled: !!symbol,
  });

  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message="Failed to load congressional trades" />;

  const trades = data?.results ?? [];
  const totalPages = data?.total_pages ?? 1;

  return (
    <section className="p-4 border-b border-dark-border">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Congressional Trades</h2>
        {(data?.count ?? 0) > 0 && (
          <span className="text-xs text-gray-400">{data?.count} disclosures</span>
        )}
      </div>
      {!trades.length ? (
        <p className="text-gray-400 text-sm">No congressional trades for {symbol}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-dark-border">
                  {['Disclosed', 'Member', 'Type', 'Amount', 'Transaction Date'].map((h) => (
                    <th key={h} className="py-1.5 pr-5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => {
                  const style = txStyle(t.transaction_type);
                  return (
                    <tr key={i} className="border-b border-dark-border/40 hover:bg-dark-bg/60 transition-colors">
                      <td className="py-1.5 pr-5 font-numeric text-gray-400">{fmtUsDate(t.disclosure_date)}</td>
                      <td className="py-1.5 pr-5 text-gray-200">{getMemberName(t)}</td>
                      <td className="py-1.5 pr-5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${style.bg} ${style.text}`}>
                          {t.transaction_type}
                        </span>
                      </td>
                      <td className="py-1.5 pr-5 font-numeric text-white">{t.amount_range || '\u2014'}</td>
                      <td className="py-1.5 pr-5 font-numeric text-gray-400">{fmtUsDate(t.transaction_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPrev={() => setPage((p) => p - 1)}
              onNext={() => setPage((p) => p + 1)}
            />
          )}
        </>
      )}
    </section>
  );
}
